export type FilterOperator =
  | '='
  | '!='
  | 'in'
  | 'not_in'
  | '>'
  | '<'
  | '>='
  | '<='
  | 'contains'
  | 'not_contains'
export type FilterField =
  | 'call_id'
  | 'duty_name'
  | 'created_at'
  | 'patient_name'
  | 'phone'
  | 'type'
  | 'duration'
  | 'ended_reason'
  | 'label'

export interface FilterCondition {
  id: string
  field: FilterField | string // Allow any string for dynamic filter fields
  operator: FilterOperator
  value: string
}

export interface FilterFieldConfig {
  value: FilterField | string
  label: string
  type: 'text' | 'number' | 'date' | 'options'
  options?: Array<{ value: string; label: string }>
}

export const filterFields: FilterFieldConfig[] = [
  { value: 'call_id', label: 'Call ID', type: 'text' },
  { value: 'duty_name', label: 'Duty Name', type: 'text' },
  { value: 'created_at', label: 'Date', type: 'date' },
  { value: 'patient_name', label: 'Client Name', type: 'text' },
  { value: 'phone', label: 'Phone', type: 'text' },
  { value: 'type', label: 'Type', type: 'text' },
  { value: 'duration', label: 'Duration (s)', type: 'number' },
  { value: 'ended_reason', label: 'Ended Reason', type: 'text' },
  { value: 'label', label: 'Label', type: 'text' },
]

/**
 * Get field configuration by field value
 */
export function getFieldConfig(
  field: FilterField
): FilterFieldConfig | undefined {
  return filterFields.find((f) => f.value === field)
}

/**
 * Get operators available for a specific field type
 */
export function getOperatorsForField(field: FilterField): FilterOperator[] {
  // Call ID only supports "=" operator
  if (field === 'call_id') {
    return ['=']
  }
  // Type only supports "=" operator (uses checkboxes, not text input)
  if (field === 'type') {
    return ['=']
  }
  // Client Name only supports "=" and "contains" operators
  if (field === 'patient_name') {
    return ['=', 'contains']
  }
  // Duty Name only supports "=" and "contains" operators
  if (field === 'duty_name') {
    return ['=', 'contains']
  }
  // Phone only supports "=" and "contains" operators
  if (field === 'phone') {
    return ['=', 'contains']
  }
  // Ended Reason only supports "=" and "contains" operators
  if (field === 'ended_reason') {
    return ['=', 'contains']
  }
  // Label only supports "=" and "contains" operators
  if (field === 'label') {
    return ['=', 'contains']
  }
  const fieldConfig = getFieldConfig(field)
  if (fieldConfig?.type === 'number') {
    return ['=', '>', '<', '>=', '<=']
  }
  if (fieldConfig?.type === 'date') {
    return ['=', '>', '<', '>=', '<=']
  }
  return ['=', 'contains', 'not_contains']
}

/**
 * Check if filters are active (have values)
 */
export function hasActiveFilters(filters: FilterCondition[]): boolean {
  return (
    filters.length > 0 &&
    filters.some((f) => {
      if (f.field === 'type') {
        // For Type, check if at least one checkbox is selected
        const selectedTypes = f.value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
        return selectedTypes.length > 0
      }
      return f.value.trim()
    })
  )
}

/**
 * Human-readable labels for filter operators
 */
export const operatorLabels: Record<FilterOperator, string> = {
  '=': '=',
  '!=': '!=',
  in: 'in',
  not_in: 'not in',
  '>': '>',
  '<': '<',
  '>=': '≥',
  '<=': '≤',
  contains: 'contains',
  not_contains: 'does not contain',
}
